import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  Grid,
  GridProps,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'

import ScreenShareIcon from '@mui/icons-material/ScreenShare'

import dialogHook from '@hooks/useDialog'
import titleHook from '@hooks/useTitle'

import { RequestDemoDialog } from '@components/RequestDemoDialog'

import {
  buttonSx,
  containerSx,
  contentSx,
  ctaTextSx,
  launchpadLogoSx,
} from './styles'

interface ILetsTalk extends GridProps {
  containerSxOverride?: SxProps<Theme>
}

const LetsTalk = ({ containerSxOverride, ...rest }: ILetsTalk) => {
  const { t } = useTranslation()
  const requestDemoDialog = dialogHook.useDialog()

  const { changePageTitle, restorePreviousPageTitle } = titleHook.useTitle(
    document.title
  )

  useEffect(() => {
    if (requestDemoDialog.isOpen) {
      changePageTitle(`${document.title} - Request a Demo`)
      return
    }

    restorePreviousPageTitle()
  }, [requestDemoDialog.isOpen, restorePreviousPageTitle, changePageTitle])

  const handleOnRequestADemoButtonClick = () => {
    requestDemoDialog.open()
  }

  return (
    <>
      {requestDemoDialog.isOpen && (
        <RequestDemoDialog
          open={requestDemoDialog.isOpen}
          onClose={requestDemoDialog.close}
        />
      )}

      <Grid
        item
        xs={12}
        lg={6}
        sx={
          {
            ...containerSx,
            ...containerSxOverride,
          } as SxProps<Theme>
        }
        {...rest}
      >
        <Box sx={contentSx}>
          <Box
            loading="lazy"
            component="img"
            src="/images/launchpad-white-logo.webp"
            sx={launchpadLogoSx}
            alt={t('Launchpad Logo')}
          />
          <Typography variant="h3" sx={ctaTextSx}>
            {`${t('Ready to take this conversation to the next level?')}`}
          </Typography>

          <Button
            data-testid="contact-us-section-request-demo-button"
            variant="contained"
            color="secondary"
            size="medium"
            sx={buttonSx}
            endIcon={<ScreenShareIcon />}
            onClick={handleOnRequestADemoButtonClick}
          >
            {t('Request a demo')}
          </Button>
        </Box>
      </Grid>
    </>
  )
}

export { LetsTalk }
